/**
 * Issue within an Alert (notifications might not have issues)
 */
export interface AlertIssue {
  issueKey: string;
  ruleId: string;
  severity: 'WARNING' | 'ERROR';
  title: string;
  actionLabel: string;
  fieldRef: string | null;
}

/**
 * Combined interface for both Alerts and Notifications
 * Differentiated by the 'category' field (discriminated union)
 *
 * List endpoint (GET /notifications) may omit `issues` for ALERT items — banners only need
 * metadata + `issueId`. Full `issues` are loaded from GET /notifications/moment/{issueId}
 * when the slideout opens and merged into Redux.
 */
export interface AlertsAndNotification {
  id: string;
  userId: string;
  ruleId: string;
  category: 'ALERT' | 'NOTIFICATION';
  severity: 'WARNING' | 'ERROR';
  actionable: boolean;
  ctaLabel: string;
  status: 'UNREAD' | 'READ';
  sticky: boolean;
  issueId: string;
  momentName: string;
  groupKey: string;
  dedupKey: string;
  uiCopy: string;
  headline: string;
  link: string;
  /** Present on moment-scoped responses; optional on global list (banner) payloads */
  issues?: AlertIssue[];
  dismissedIssueKeys: string[];
  createdAt: string;
}

/**
 * API Response - array of Alerts and/or Notifications
 */
export type AlertsAndNotificationsResponse = AlertsAndNotification[];

/**
 * API Response for moment-specific notifications endpoint
 * GET /api/v2/notifications/moment/{issueId}
 */
export interface MomentNotificationsResponse {
  notifications: AlertsAndNotification[];
  unreadCount: number;
  alertCount: number;
  criticalCount: number;
  syncStatus: 'FRESH' | 'STALE';
  momentVersion: number;
  notificationVersion: number;
}

/**
 * Type guard to check if item is an Alert
 */
export const isAlert = (
  item: AlertsAndNotification
): item is AlertsAndNotification & { category: 'ALERT' } => {
  return item.category === 'ALERT';
};

/**
 * Type guard to check if item is a Notification
 */
export const isNotification = (
  item: AlertsAndNotification
): item is AlertsAndNotification & { category: 'NOTIFICATION' } => {
  return item.category === 'NOTIFICATION';
};

/**
 * State structure for managing both Alerts and Notifications
 */
export interface AlertsAndNotificationsState {
  loading: boolean;
  error: any | null;
  data: AlertsAndNotification[];
  alerts: AlertsAndNotification[];
  notifications: AlertsAndNotification[];
  /** Index from GET /notifications only — banners; not used for slideout bell/modal/highlights */
  alertsByIssueId: Record<string, AlertsAndNotification>;
  notificationsById: Record<string, AlertsAndNotification>;
  /** GET /notifications/moment/{issueId} only — slideout bell, modal, field highlights */
  momentAlertsByIssueId: Record<string, AlertsAndNotification>;
  /**
   * Client-side computed alerts for overdue features (target date in the past AND status not
   * Closed/Completed). Kept in a separate slice key so they coexist with server-loaded alerts
   * without being overwritten by the notifications API load, and so `dismissAlert` (which only
   * touches `data`/`alerts`/`notifications`) never removes them — they are re-derived instead.
   */
  overdueAlerts: AlertsAndNotification[];
  // Tracks fieldRefs that have been cleared/updated (should NOT be highlighted)
  clearedFieldRefs: Record<string, string[]>;
  // Store alertCount per moment (from MomentNotificationsResponse)
  alertCountByIssueId: Record<string, number>;
}
